import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Download, LogOut, RefreshCw, ArrowLeft, Loader2, KeyRound, CheckCircle2, Mail, FileText, Briefcase, Trash2, ListChecks, CalendarClock } from "lucide-react";
import { API } from "@/lib/stream";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const timeAgo = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const inputCls =
  "w-full bg-white rounded-2xl border-2 border-slate-900/10 px-4 py-3 text-sm font-medium outline-none focus:border-[#FF5722] transition-colors placeholder:text-slate-400";

const fmtUsd = (n) => (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const STATUSES = [
  { value: "new", label: "New", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  { value: "contacted", label: "Contacted", cls: "bg-blue-50 text-[#2563EB] border-blue-200" },
  { value: "demo-booked", label: "Demo booked", cls: "bg-amber-50 text-[#B45309] border-amber-200" },
  { value: "closed", label: "Closed", cls: "bg-emerald-50 text-[#047857] border-emerald-200" },
];

const EMAIL_FILTERS = [
  { value: "all", label: "All", color: "#64748B" },
  { value: "alert", label: "Alerts", color: "#38BDF8" },
  { value: "offer", label: "Offers", color: "#F59E0B" },
  { value: "invite", label: "Invites", color: "#2563EB" },
  { value: "digest", label: "Digests", color: "#10B981" },
  { value: "other", label: "Other", color: "#8B5CF6" },
];

export default function AdminCRM() {
  const location = useLocation();
  const [access, setAccess] = useState(localStorage.getItem("op360_access") || "");
  const [leads, setLeads] = useState(null);
  const [vault, setVault] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [emailFilter, setEmailFilter] = useState("all");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.authError || "");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwMsg, setPwMsg] = useState(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");
  const [emailFor, setEmailFor] = useState(null);
  const [emailLead, setEmailLead] = useState("");
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [prEmployees, setPrEmployees] = useState(12);
  const [prSalary, setPrSalary] = useState(52000);
  const [prTax, setPrTax] = useState(4);
  const [prLead, setPrLead] = useState("");
  const [prMsg, setPrMsg] = useState(null);
  const [prBusy, setPrBusy] = useState(false);

  const authFetch = useCallback(async (path, options = {}) => {
    const withAuth = (token) => ({
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
    });
    let res = await fetch(`${API}${path}`, withAuth(access));
    if (res.status === 401) {
      const rt = localStorage.getItem("op360_refresh");
      if (rt) {
        const r = await fetch(`${API}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: rt }),
        });
        if (r.ok) {
          const data = await r.json();
          localStorage.setItem("op360_access", data.access_token);
          setAccess(data.access_token);
          res = await fetch(`${API}${path}`, withAuth(data.access_token));
        }
      }
    }
    return res;
  }, [access]);

  const load = useCallback(async () => {
    const res = await authFetch("/admin/leads");
    if (res.ok) {
      const data = await res.json();
      setLeads(data.leads || []);
    } else if (res.status === 401) {
      setAccess("");
      localStorage.removeItem("op360_access");
    }
    const v = await authFetch("/admin/vault");
    if (v.ok) {
      const vd = await v.json();
      setVault(vd.documents || []);
    }
    const rq = await authFetch("/admin/requisitions");
    if (rq.ok) {
      const rd = await rq.json();
      setReqs(rd.requisitions || []);
    }
    const fn = await authFetch("/admin/funnel");
    if (fn.ok) {
      const fd = await fn.json();
      setFunnel(Object.fromEntries((fd.funnel || []).map((f) => [f.req_id, f])));
    }
    const el = await authFetch("/admin/email-log");
    if (el.ok) {
      const ed = await el.json();
      setEmailLog(ed.emails || []);
    }
  }, [authFetch]);

  useEffect(() => {
    if (access) load();
  }, [access, load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(formatApiErrorDetail(data.detail));
      } else {
        localStorage.setItem("op360_access", data.access_token);
        localStorage.setItem("op360_refresh", data.refresh_token);
        setAccess(data.access_token);
      }
    } catch (err) {
      setError("Could not reach the server.");
    }
    setBusy(false);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = () => {
    localStorage.removeItem("op360_access");
    localStorage.removeItem("op360_refresh");
    setAccess("");
    setLeads(null);
  };

  const exportCsv = async () => {
    const res = await authFetch("/admin/leads/export");
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "opera360-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDoc = async (doc) => {
    const res = await authFetch(`/admin/vault/${doc.id}/download`);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.original_filename || "document.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const setStatus = async (leadId, status) => {
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
    await authFetch(`/admin/leads/${leadId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  };

  const REQ_STATUSES = [
    { value: "open", label: "Open", cls: "bg-slate-100 text-slate-600 border-slate-300" },
    { value: "approved", label: "Approved", cls: "bg-blue-50 text-[#2563EB] border-blue-200" },
    { value: "on-hold", label: "On hold", cls: "bg-amber-50 text-[#B45309] border-amber-200" },
    { value: "filled", label: "Filled", cls: "bg-emerald-50 text-[#047857] border-emerald-200" },
  ];

  const setReqStatus = async (reqId, status) => {
    setReqs((rs) => rs.map((r) => (r.id === reqId ? { ...r, status } : r)));
    await authFetch(`/admin/requisitions/${reqId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  };

  const [appsFor, setAppsFor] = useState(null);
  const [apps, setApps] = useState([]);
  const [funnel, setFunnel] = useState({});
  const [offerBusy, setOfferBusy] = useState(null);
  const [offerMsg, setOfferMsg] = useState(null);
  const [schedFor, setSchedFor] = useState(null);
  const [schedForm, setSchedForm] = useState({ proposed_at: "", duration_minutes: 30, mode: "video", note: "" });
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedMsg, setSchedMsg] = useState(null);

  const scheduleInterview = async (appId) => {
    if (!schedForm.proposed_at || schedBusy) return;
    setSchedBusy(true);
    setSchedMsg(null);
    try {
      const res = await authFetch("/admin/interviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: appId, ...schedForm }),
      });
      const d = await res.json();
      if (res.ok) {
        setSchedMsg({ ok: true, text: `Invite emailed to ${d.to} — ${d.when} at ${d.time}` });
        setApps((as) => as.map((a) => (a.id === appId ? { ...a, interview_at: schedForm.proposed_at, interview_mode: schedForm.mode } : a)));
        setSchedFor(null);
      } else {
        setSchedMsg({ ok: false, text: formatApiErrorDetail(d.detail) });
      }
    } catch {
      setSchedMsg({ ok: false, text: "Could not reach the server." });
    }
    setSchedBusy(false);
    setTimeout(() => setSchedMsg(null), 7000);
  };

  const sendOffer = async (appId) => {
    setOfferBusy(appId);
    setOfferMsg(null);
    try {
      const res = await authFetch(`/admin/applications/${appId}/send-offer`, { method: "POST" });
      const d = await res.json();
      if (res.ok) {
        setOfferMsg({ ok: true, text: `Offer letter emailed to ${d.to}` });
        setApps((as) => as.map((a) => (a.id === appId ? { ...a, offer_email_sent: true } : a)));
      } else {
        setOfferMsg({ ok: false, text: formatApiErrorDetail(d.detail) });
      }
    } catch {
      setOfferMsg({ ok: false, text: "Could not reach the server." });
    }
    setOfferBusy(null);
    setTimeout(() => setOfferMsg(null), 6000);
  };
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");

  const APP_STATUSES = [
    { value: "new", label: "New", cls: "bg-slate-100 text-slate-600 border-slate-300" },
    { value: "screening", label: "Screening", cls: "bg-violet-50 text-[#8B5CF6] border-violet-200" },
    { value: "interview", label: "Interview", cls: "bg-blue-50 text-[#2563EB] border-blue-200" },
    { value: "offer", label: "Offer", cls: "bg-amber-50 text-[#B45309] border-amber-200" },
    { value: "hired", label: "Hired", cls: "bg-emerald-50 text-[#047857] border-emerald-200" },
    { value: "rejected", label: "Rejected", cls: "bg-red-50 text-red-600 border-red-200" },
  ];

  const setAppStatus = async (appId, status) => {
    setApps((as) => as.map((a) => (a.id === appId ? { ...a, status } : a)));
    await authFetch(`/admin/applications/${appId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  };

  const saveNote = async (leadId) => {
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, notes: noteText } : l)));
    setNoteFor(null);
    await authFetch(`/admin/leads/${leadId}/notes`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes: noteText }),
    });
  };

  const deleteLead = async (leadId, company) => {
    if (!window.confirm(`Delete ${company} from the pipeline? This cannot be undone.`)) return;
    setLeads((ls) => ls.filter((l) => l.id !== leadId));
    await authFetch(`/admin/leads/${leadId}`, { method: "DELETE" });
  };

  const [onboardBusy, setOnboardBusy] = useState(null);
  const [onboardMsg, setOnboardMsg] = useState(null);

  const onboardApplicant = async (appId) => {
    setOnboardBusy(appId);
    setOnboardMsg(null);
    try {
      const res = await authFetch(`/admin/applications/${appId}/onboard`, { method: "POST" });
      const d = await res.json();
      if (res.ok) {
        setOnboardMsg({ ok: true, text: `Checklist created: ${d.document.title} — ${d.email_sent ? `emailed to the new hire and saved` : "saved"} in the Document Vault above` });
        setApps((as) => as.map((a) => (a.id === appId ? { ...a, onboarding_file_id: d.document.id } : a)));
        load();
      } else {
        setOnboardMsg({ ok: false, text: formatApiErrorDetail(d.detail) });
      }
    } catch {
      setOnboardMsg({ ok: false, text: "Could not reach the server." });
    }
    setOnboardBusy(null);
    setTimeout(() => setOnboardMsg(null), 8000);
  };

  const toggleApps = async (reqId) => {
    if (appsFor === reqId) {
      setAppsFor(null);
      return;
    }
    setAppsFor(reqId);
    setApps([]);
    const res = await authFetch(`/admin/applications?req_id=${reqId}`);
    if (res.ok) {
      const d = await res.json();
      setApps(d.applications || []);
    }
  };

  const sendDigest = async () => {
    setDigestMsg("Sending…");
    const res = await authFetch("/admin/send-digest", { method: "POST" });
    setDigestMsg(res.ok ? "Digest sent to your inbox" : "Send failed — try again");
    setTimeout(() => setDigestMsg(""), 4000);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    setPwMsg(null);
    try {
      const res = await authFetch("/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: curPw, new_password: newPw }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwMsg({ ok: true, text: "Password updated. Use it on your next sign-in." });
        setCurPw("");
        setNewPw("");
      } else {
        setPwMsg({ ok: false, text: formatApiErrorDetail(data.detail) });
      }
    } catch {
      setPwMsg({ ok: false, text: "Could not reach the server." });
    }
    setPwBusy(false);
  };

  const emailDoc = async (fileId) => {
    if (!emailLead || emailBusy) return;
    setEmailBusy(true);
    setEmailMsg(null);
    try {
      const res = await authFetch(`/admin/vault/${fileId}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: emailLead }),
      });
      const d = await res.json();
      setEmailMsg(res.ok ? { ok: true, text: `Document emailed to ${d.to}` } : { ok: false, text: formatApiErrorDetail(d.detail) });
      if (res.ok) setEmailFor(null);
    } catch {
      setEmailMsg({ ok: false, text: "Could not reach the server." });
    }
    setEmailBusy(false);
    setTimeout(() => setEmailMsg(null), 5000);
  };

  const emailPayroll = async () => {
    if (!prLead || prBusy) return;
    setPrBusy(true);
    setPrMsg(null);
    try {
      const res = await authFetch("/admin/email-payroll", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: prLead, employees: prEmployees, salary: prSalary, state_tax: prTax }),
      });
      const d = await res.json();
      setPrMsg(res.ok
        ? { ok: true, text: `Estimate emailed to ${d.to} — ${fmtUsd(d.per_run)} per run, ${fmtUsd(d.annual)} annual` }
        : { ok: false, text: formatApiErrorDetail(d.detail) });
    } catch {
      setPrMsg({ ok: false, text: "Could not reach the server." });
    }
    setPrBusy(false);
    setTimeout(() => setPrMsg(null), 6000);
  };

  const emailCounts = EMAIL_FILTERS.reduce((acc, f) => {
    acc[f.value] = f.value === "all" ? emailLog.length : emailLog.filter((e) => (e.kind || "other") === f.value).length;
    return acc;
  }, {});
  const visibleEmailLog = emailFilter === "all" ? emailLog : emailLog.filter((e) => (e.kind || "other") === emailFilter);

  if (!access) {
    return (
      <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md rounded-3xl border-2 border-slate-900/5 bg-white p-8 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.2)] space-y-4"
        >
          <a href="/" className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#FF5722] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </a>
          <div className="flex items-center gap-3 pt-1">
            <img src="/logo.png" alt="Opera360 AI" className="w-10 h-10" />
            <div>
              <div className="font-display font-black text-xl">CRM Admin</div>
              <div className="text-xs font-bold text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Private — Opera360 team only</div>
            </div>
          </div>

          <button
            data-testid="admin-google-login-button"
            type="button"
            onClick={googleLogin}
            className="w-full inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-full border-2 border-slate-900/10 bg-white font-bold text-sm text-slate-700 hover:border-[#2563EB] hover:text-[#2563EB] transition-colors duration-300"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Sign in with Google
          </button>

          <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
            <span className="flex-1 h-px bg-slate-200" /> or with email <span className="flex-1 h-px bg-slate-200" />
          </div>

          <form data-testid="admin-login-form" onSubmit={login} className="space-y-4">
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" className={inputCls} />
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} />
            {error && <div data-testid="admin-error-message" className="text-sm font-bold text-red-600">{error}</div>}
            <button data-testid="admin-login-button" type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#0F172A] text-white font-bold text-sm hover:bg-[#FF5722] transition-colors duration-300 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {busy ? "Signing in…" : "Sign in to the pipeline"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBF9]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Opera360 AI" className="w-10 h-10" />
            <div>
              <div className="font-display font-black text-2xl">CRM Pipeline</div>
              <div className="text-xs font-bold text-slate-500">{leads ? `${leads.length} companies` : "Loading…"} · full emails visible to admin</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="/" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-slate-900/10 text-xs font-bold text-slate-600 hover:border-[#2563EB] hover:text-[#2563EB] transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Site
            </a>
            <button onClick={load} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-slate-900/10 text-xs font-bold text-slate-600 hover:border-[#10B981] hover:text-[#10B981] transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button data-testid="admin-export-button" onClick={exportCsv} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#10B981] text-white text-xs font-bold hover:bg-[#0F172A] transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button data-testid="admin-digest-button" onClick={sendDigest} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#F59E0B] text-white text-xs font-bold hover:bg-[#0F172A] transition-colors">
              <Mail className="w-3.5 h-3.5" /> Email me the weekly digest
            </button>
            <button
              data-testid="admin-change-password-toggle"
              onClick={() => { setShowPw(!showPw); setPwMsg(null); }}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-slate-900/10 text-xs font-bold text-slate-600 hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-colors"
            >
              <KeyRound className="w-3.5 h-3.5" /> Password
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#0F172A] text-white text-xs font-bold hover:bg-[#FF5722] transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </div>

        {digestMsg && (
          <div data-testid="admin-digest-message" className="mt-4 text-sm font-bold text-[#B45309]">{digestMsg}</div>
        )}

        <AnimatePresence>
          {showPw && (
            <motion.form
              data-testid="admin-change-password-form"
              onSubmit={changePassword}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-6 rounded-3xl border-2 border-[#8B5CF6]/20 bg-white p-6 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)]">
                <div className="font-display font-bold text-base flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#8B5CF6]" /> Change admin password</div>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <input data-testid="admin-current-password-input" type="password" required value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="Current password" className={inputCls} />
                  <input data-testid="admin-new-password-input" type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 8 characters)" className={inputCls} />
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <button data-testid="admin-change-password-submit" type="submit" disabled={pwBusy} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#8B5CF6] text-white text-xs font-bold hover:bg-[#0F172A] transition-colors disabled:opacity-60">
                    {pwBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                    Update password
                  </button>
                  {pwMsg && (
                    <span data-testid="admin-password-message" className={`text-sm font-bold flex items-center gap-1.5 ${pwMsg.ok ? "text-[#10B981]" : "text-red-600"}`}>
                      {pwMsg.ok && <CheckCircle2 className="w-4 h-4" />}{pwMsg.text}
                    </span>
                  )}
                </div>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        <div data-testid="admin-panel" className="mt-8 rounded-3xl border-2 border-slate-900/5 bg-white shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)] overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="admin-leads-table" className="w-full text-sm min-w-[1000px]">
              <thead>
                <tr className="bg-[#0F172A] text-white text-left text-xs uppercase tracking-wider">
                  <th className="px-5 py-4 font-bold">Company</th>
                  <th className="px-5 py-4 font-bold">Contact</th>
                  <th className="px-5 py-4 font-bold">Email</th>
                  <th className="px-5 py-4 font-bold">Team</th>
                  <th className="px-5 py-4 font-bold">Pain Point</th>
                  <th className="px-5 py-4 font-bold">Score</th>
                  <th className="px-5 py-4 font-bold">Status</th>
                  <th className="px-5 py-4 font-bold">When</th>
                </tr>
              </thead>
              <tbody>
                {(leads || []).map((l, i) => {
                  const st = STATUSES.find((s) => s.value === l.status) || STATUSES[0];
                  return (
                    <tr key={l.id} className={i % 2 ? "bg-[#F4F4F0]" : "bg-white"}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="min-w-0">
                            <div className="font-bold text-slate-900">{l.company_name}</div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">via {l.source === "ziggy-chat" ? "Ziggy chat" : "website"}</div>
                          </div>
                          <button
                            data-testid={`admin-lead-note-${i}`}
                            title="Annotate lead"
                            onClick={() => { setNoteFor(noteFor === l.id ? null : l.id); setNoteText(l.notes || ""); }}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-[#8B5CF6] hover:bg-violet-50 transition-colors shrink-0"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`admin-lead-delete-${i}`}
                            title="Delete lead"
                            onClick={() => deleteLead(l.id, l.company_name)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {l.notes && noteFor !== l.id && <div className="text-xs text-slate-500 mt-1 truncate max-w-[220px]">{l.notes}</div>}
                        {noteFor === l.id && (
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              data-testid="admin-lead-note-input"
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              placeholder="Add a note…"
                              className="flex-1 bg-white rounded-xl border-2 border-[#8B5CF6]/30 px-3 py-1.5 text-xs font-medium outline-none focus:border-[#8B5CF6]"
                              onKeyDown={(e) => e.key === "Enter" && saveNote(l.id)}
                            />
                            <button data-testid="admin-lead-note-save" onClick={() => saveNote(l.id)} className="px-3 py-1.5 rounded-full bg-[#8B5CF6] text-white text-[11px] font-bold hover:bg-[#0F172A] transition-colors">Save</button>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-slate-600">{l.contact_name}</td>
                      <td className="px-5 py-3.5 font-semibold text-[#2563EB]">{l.work_email}</td>
                      <td className="px-5 py-3.5 text-slate-600">{l.team_size}</td>
                      <td className="px-5 py-3.5 text-slate-600">{l.pain_point}</td>
                      <td className="px-5 py-3.5 font-display font-black text-[#FF5722]">{l.score}</td>
                      <td className="px-5 py-3.5">
                        <select
                          data-testid={`admin-lead-status-${i}`}
                          value={l.status}
                          onChange={(e) => setStatus(l.id, e.target.value)}
                          className={`text-xs font-bold rounded-full border-2 px-3 py-1.5 outline-none cursor-pointer transition-colors ${st.cls}`}
                        >
                          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-3.5 text-slate-500 text-xs font-bold">{timeAgo(l.created_at)}</td>
                    </tr>
                  );
                })}
                {leads && leads.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-14 text-center text-slate-500 font-medium">No leads yet.</td></tr>
                )}
                {!leads && (
                  <tr><td colSpan={8} className="px-5 py-14 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div data-testid="admin-vault-list" className="mt-8 rounded-3xl border-2 border-slate-900/5 bg-white p-7 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)]">
          <div className="flex items-center justify-between">
            <div className="font-display font-black text-xl flex items-center gap-2"><FileText className="w-5 h-5 text-[#8B5CF6]" /> Document Vault</div>
            <span className="text-xs font-bold text-slate-400">{vault.length} stored {vault.length === 1 ? "document" : "documents"}</span>
          </div>
          <p className="text-xs font-semibold text-slate-400 mt-1">Documents visitors file with the "Vault" button in the Claude drafter land here.</p>
          {emailMsg && (
          <div data-testid="admin-vault-email-message" className={`mt-4 text-sm font-bold ${emailMsg.ok ? "text-[#10B981]" : "text-red-600"}`}>{emailMsg.text}</div>
        )}
        <div className="mt-4 space-y-2.5">
            {vault.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Nothing vaulted yet.</div>}
            {vault.map((d, i) => (
              <div key={d.id}>
                <div className="flex items-center gap-4 rounded-2xl bg-[#F4F4F0] px-4 py-3">
                  <FileText className="w-4 h-4 text-[#8B5CF6] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-800 truncate">{d.title}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{d.doc_type} · {(d.size / 1024).toFixed(1)} KB · {timeAgo(d.created_at)}</div>
                  </div>
                  <button
                    data-testid={`admin-vault-email-${i}`}
                    onClick={() => { setEmailFor(emailFor === d.id ? null : d.id); setEmailLead(""); }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#EC4899] text-white text-xs font-bold hover:bg-[#0F172A] transition-colors shrink-0"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email
                  </button>
                  <button
                    data-testid={`admin-vault-download-${i}`}
                    onClick={() => downloadDoc(d)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0F172A] text-white text-xs font-bold hover:bg-[#8B5CF6] transition-colors shrink-0"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </div>
                {emailFor === d.id && (
                  <div className="mt-2 ml-8 flex flex-wrap items-center gap-2.5 rounded-2xl border-2 border-[#EC4899]/20 bg-white px-4 py-3">
                    <span className="text-xs font-bold text-slate-500">Send to lead:</span>
                    <select
                      data-testid="admin-vault-email-lead-select"
                      value={emailLead}
                      onChange={(e) => setEmailLead(e.target.value)}
                      className="flex-1 min-w-[220px] bg-[#F4F4F0] rounded-xl border-2 border-slate-900/10 px-3 py-2 text-xs font-medium outline-none focus:border-[#EC4899]"
                    >
                      <option value="">Choose a company…</option>
                      {(leads || []).map((l) => (
                        <option key={l.id} value={l.id}>{l.company_name} — {l.work_email}</option>
                      ))}
                    </select>
                    <button
                      data-testid="admin-vault-email-submit"
                      onClick={() => emailDoc(d.id)}
                      disabled={!emailLead || emailBusy}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#EC4899] text-white text-xs font-bold hover:bg-[#0F172A] transition-colors disabled:opacity-50"
                    >
                      {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Send
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div data-testid="admin-payroll-estimator" className="mt-8 rounded-3xl border-2 border-slate-900/5 bg-white p-7 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)]">
          <div className="font-display font-black text-xl flex items-center gap-2"><Mail className="w-5 h-5 text-[#2563EB]" /> Email a payroll estimate</div>
          <p className="text-xs font-semibold text-slate-400 mt-1">Send a branded payroll run estimate to any company in the pipeline.</p>
          <div className="grid sm:grid-cols-4 gap-4 mt-5">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Team members</label>
              <input data-testid="admin-payroll-est-employees" type="number" min="1" value={prEmployees} onChange={(e) => setPrEmployees(Number(e.target.value) || 1)} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Avg salary</label>
              <input data-testid="admin-payroll-est-salary" type="number" min="10000" step="1000" value={prSalary} onChange={(e) => setPrSalary(Number(e.target.value) || 10000)} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">State tax %</label>
              <input data-testid="admin-payroll-est-tax" type="number" min="0" max="25" step="0.5" value={prTax} onChange={(e) => setPrTax(Number(e.target.value) || 0)} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Send to lead</label>
              <select data-testid="admin-payroll-est-lead-select" value={prLead} onChange={(e) => setPrLead(e.target.value)} className={`${inputCls} mt-1.5`}>
                <option value="">Choose a company…</option>
                {(leads || []).map((l) => (
                  <option key={l.id} value={l.id}>{l.company_name} — {l.work_email}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <button
              data-testid="admin-payroll-est-submit"
              onClick={emailPayroll}
              disabled={!prLead || prBusy}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#2563EB] text-white text-xs font-bold hover:bg-[#0F172A] transition-colors disabled:opacity-50"
            >
              {prBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              Send estimate
            </button>
            {prMsg && (
              <span data-testid="admin-payroll-est-message" className={`text-sm font-bold ${prMsg.ok ? "text-[#10B981]" : "text-red-600"}`}>{prMsg.text}</span>
            )}
          </div>
        </div>

        <div data-testid="admin-email-log-card" className="mt-8 rounded-3xl border-2 border-slate-900/5 bg-white p-7 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-display font-black text-xl flex items-center gap-2"><Mail className="w-5 h-5 text-[#F59E0B]" /> Email Activity</div>
              <p className="text-xs font-semibold text-slate-400 mt-1">Every estimate, document, alert, invite and digest the system has emailed.</p>
            </div>
            <span className="text-xs font-bold text-slate-400">{visibleEmailLog.length} shown · {emailLog.length} recent sends</span>
          </div>
          <div className="mt-5 flex flex-wrap gap-2" aria-label="Filter email activity by type">
            {EMAIL_FILTERS.map((f) => {
              const active = emailFilter === f.value;
              return (
                <button
                  key={f.value}
                  data-testid={`admin-email-filter-${f.value}`}
                  type="button"
                  onClick={() => setEmailFilter(f.value)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-xs font-bold transition-all duration-300 ${
                    active ? "border-transparent text-white shadow-md" : "border-slate-900/10 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                  style={active ? { background: f.color } : undefined}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: active ? "#FFFFFF" : f.color }} />
                  {f.label}
                  <span className={`text-[10px] font-black ${active ? "text-white/80" : "text-slate-400"}`}>{emailCounts[f.value] || 0}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-2 max-h-72 overflow-y-auto chat-scroll pr-1">
            {visibleEmailLog.length === 0 && (
              <div data-testid="admin-email-log-empty" className="text-sm text-slate-400 py-6 text-center">
                {emailLog.length === 0 ? "No emails logged yet — sends from here on will appear." : `No ${EMAIL_FILTERS.find((f) => f.value === emailFilter)?.label.toLowerCase()} in the recent activity.`}
              </div>
            )}
            {visibleEmailLog.map((e, i) => {
              const meta = EMAIL_FILTERS.find((f) => f.value === (e.kind || "other")) || EMAIL_FILTERS[EMAIL_FILTERS.length - 1];
              return (
                <div key={`${e.email_id || e.at}-${i}`} data-testid={`admin-email-log-row-${i}`} className="flex items-center gap-4 rounded-2xl bg-[#F4F4F0] px-4 py-3">
                  <Mail className="w-4 h-4 shrink-0" style={{ color: meta.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-800 truncate">{e.subject}</div>
                    <div className="text-xs font-semibold text-[#2563EB] truncate">{e.to}</div>
                  </div>
                  <span data-testid={`admin-email-log-kind-${i}`} className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white shrink-0" style={{ color: meta.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />{meta.label}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider shrink-0">{timeAgo(e.at)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div data-testid="admin-requisitions-card" className="mt-8 rounded-3xl border-2 border-slate-900/5 bg-white p-7 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)]">          <div className="flex items-center justify-between">
            <div className="font-display font-black text-xl flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#FF5722]" /> Hiring Requisitions</div>
            <span className="text-xs font-bold text-slate-400">{reqs.length} filed</span>
          </div>
          <p className="text-xs font-semibold text-slate-400 mt-1">Requisitions filed from the Recruiting section on the site.</p>
          <div className="mt-4 space-y-2.5">
            {reqs.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No requisitions yet.</div>}
            {reqs.map((r, i) => (
              <div key={r.id} data-testid={`admin-req-row-${i}`} className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-2xl bg-[#F4F4F0] px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-slate-800">{r.role}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {r.department} · {r.openings} opening{r.openings > 1 ? "s" : ""} · {r.hiring_manager || "Manager TBD"} · {fmtUsd(r.salary_min)}–{fmtUsd(r.salary_max)} · {timeAgo(r.created_at)}
                  </div>
                  {r.justification && <div className="text-xs text-slate-500 mt-1 truncate max-w-xl">{r.justification}</div>}
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${r.priority === "critical" ? "bg-red-100 text-red-700" : r.priority === "high" ? "bg-amber-100 text-[#B45309]" : "bg-slate-200 text-slate-600"}`}>{r.priority}</span>
                <button
                  data-testid={`admin-req-applicants-${i}`}
                  onClick={() => toggleApps(r.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-slate-900/10 text-xs font-bold text-slate-600 hover:border-[#FF5722] hover:text-[#FF5722] transition-colors"
                >
                  Applicants
                </button>
                <select
                  data-testid={`admin-req-status-${i}`}
                  value={r.status || "open"}
                  onChange={(e) => setReqStatus(r.id, e.target.value)}
                  className={`text-xs font-bold rounded-full border-2 px-3 py-1.5 outline-none cursor-pointer transition-colors ${(REQ_STATUSES.find((s) => s.value === (r.status || "open")) || REQ_STATUSES[0]).cls}`}
                >
                  {REQ_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                {funnel[r.id] && funnel[r.id].applied > 0 && (
                  <div data-testid={`admin-req-funnel-${i}`} className="w-full flex items-center gap-1.5 mt-2.5">
                    {[["Applied", funnel[r.id].applied, "#64748B"], ["Screening", funnel[r.id].screening, "#8B5CF6"], ["Interview", funnel[r.id].interview, "#2563EB"], ["Offer", funnel[r.id].offer, "#F59E0B"], ["Hired", funnel[r.id].hired, "#10B981"]].map(([label, n, color]) => (
                      <div key={label} className="flex-1 min-w-0">
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${funnel[r.id].applied ? Math.max(6, (n / funnel[r.id].applied) * 100) : 0}%`, background: color }} />
                        </div>
                        <div className="text-[9px] font-bold uppercase tracking-wider mt-1 truncate" style={{ color }}>{n} {label}</div>
                      </div>
                    ))}
                    {funnel[r.id].rejected > 0 && <span className="text-[9px] font-bold uppercase tracking-wider text-red-400 shrink-0">{funnel[r.id].rejected} declined</span>}
                  </div>
                )}
              </div>
            ))}
            {appsFor && (
              <div data-testid="admin-applicants-panel" className="rounded-2xl border-2 border-[#FF5722]/20 bg-white px-5 py-4">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                  Applicants for {reqs.find((r) => r.id === appsFor)?.role || "requisition"}
                </div>
                {apps.length === 0 && <div className="text-sm text-slate-400 py-3">No applicants yet.</div>}
                {onboardMsg && (
                  <div data-testid="admin-onboard-message" className={`mb-3 text-xs font-bold ${onboardMsg.ok ? "text-[#10B981]" : "text-red-600"}`}>{onboardMsg.text}</div>
                )}
                {offerMsg && (
                  <div data-testid="admin-offer-message" className={`mb-3 text-xs font-bold ${offerMsg.ok ? "text-[#10B981]" : "text-red-600"}`}>{offerMsg.text}</div>
                )}
                <div className="space-y-2">
                  {apps.map((a, i) => (
                    <div key={a.id} data-testid={`admin-applicant-row-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-[#F4F4F0] px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-slate-800">{a.name}</div>
                        <div className="text-xs font-semibold text-[#2563EB]">{a.email}</div>
                        {a.note && <div className="text-xs text-slate-500 mt-0.5 truncate max-w-lg">{a.note}</div>}
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{timeAgo(a.created_at)}</span>
                      <select
                        data-testid={`admin-applicant-status-${i}`}
                        value={a.status || "new"}
                        onChange={(e) => setAppStatus(a.id, e.target.value)}
                        className={`text-xs font-bold rounded-full border-2 px-3 py-1.5 outline-none cursor-pointer transition-colors ${(APP_STATUSES.find((s) => s.value === (a.status || "new")) || APP_STATUSES[0]).cls}`}
                      >
                        {APP_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      {a.status === "hired" && !a.onboarding_file_id && (
                        <button
                          data-testid={`admin-applicant-onboard-${i}`}
                          onClick={() => onboardApplicant(a.id)}
                          disabled={onboardBusy === a.id}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#10B981] text-white text-[11px] font-bold hover:bg-[#0F172A] transition-colors disabled:opacity-50"
                        >
                          {onboardBusy === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListChecks className="w-3 h-3" />}
                          {onboardBusy === a.id ? "Claude drafting…" : "Onboard"}
                        </button>
                      )}
                      {a.onboarding_file_id && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#10B981] uppercase tracking-wider"><CheckCircle2 className="w-3 h-3" /> Onboarded</span>
                      )}
                      {a.interview_kit_file_id && (
                        <button
                          data-testid={`admin-applicant-kit-${i}`}
                          onClick={() => downloadDoc({ id: a.interview_kit_file_id, original_filename: `interview-kit-${a.name.replace(/\s+/g, "-")}.txt` })}
                          title="Gemini interview kit (auto-attached at Interview stage)"
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#10B981] text-white text-[11px] font-bold hover:bg-[#0F172A] transition-colors"
                        >
                          <Download className="w-3 h-3" /> Kit
                        </button>
                      )}
                      {a.offer_letter_file_id && (
                        <button
                          data-testid={`admin-applicant-offer-${i}`}
                          onClick={() => downloadDoc({ id: a.offer_letter_file_id, original_filename: `offer-letter-${a.name.replace(/\s+/g, "-")}.txt` })}
                          title="Claude offer letter (auto-drafted at Offer stage)"
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#F59E0B] text-white text-[11px] font-bold hover:bg-[#0F172A] transition-colors"
                        >
                          <Download className="w-3 h-3" /> Offer
                        </button>
                      )}
                      {a.offer_letter_file_id && !a.offer_email_sent && (
                        <button
                          data-testid={`admin-applicant-send-offer-${i}`}
                          onClick={() => sendOffer(a.id)}
                          disabled={offerBusy === a.id}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#EC4899] text-white text-[11px] font-bold hover:bg-[#0F172A] transition-colors disabled:opacity-50"
                        >
                          {offerBusy === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                          Email offer
                        </button>
                      )}
                      {a.offer_email_sent && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#10B981] uppercase tracking-wider"><CheckCircle2 className="w-3 h-3" /> Offer emailed</span>
                      )}
                      {a.rejection_sent && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-400 uppercase tracking-wider">Decline sent</span>
                      )}
                      {a.interview_at && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#2563EB] uppercase tracking-wider"><CalendarClock className="w-3 h-3" /> {new Date(a.interview_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                      )}
                      <button
                        data-testid={`admin-applicant-schedule-${i}`}
                        onClick={() => { setSchedFor(schedFor === a.id ? null : a.id); setSchedMsg(null); }}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border-2 border-[#2563EB]/40 text-[#2563EB] text-[11px] font-bold hover:bg-[#2563EB] hover:text-white transition-colors"
                      >
                        <CalendarClock className="w-3 h-3" /> Schedule
                      </button>
                      {a.resume_file_id && (
                        <button
                          data-testid={`admin-applicant-resume-${i}`}
                          onClick={() => downloadDoc({ id: a.resume_file_id, original_filename: `resume-${a.name.replace(/\s+/g, "-")}.pdf` })}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#8B5CF6] text-white text-[11px] font-bold hover:bg-[#0F172A] transition-colors"
                        >
                          <Download className="w-3 h-3" /> Resume
                        </button>
                      )}
                      {schedFor === a.id && (
                        <div data-testid="admin-schedule-form" className="w-full mt-2.5 rounded-xl border-2 border-[#2563EB]/20 bg-white p-4 flex flex-wrap items-center gap-2.5">
                          <input
                            data-testid="admin-schedule-datetime"
                            type="datetime-local"
                            value={schedForm.proposed_at}
                            onChange={(e) => setSchedForm({ ...schedForm, proposed_at: e.target.value })}
                            className="bg-[#F4F4F0] rounded-xl border-2 border-slate-900/10 px-3 py-2 text-xs font-medium outline-none focus:border-[#2563EB]"
                          />
                          <select data-testid="admin-schedule-duration" value={schedForm.duration_minutes} onChange={(e) => setSchedForm({ ...schedForm, duration_minutes: Number(e.target.value) })} className="bg-[#F4F4F0] rounded-xl border-2 border-slate-900/10 px-3 py-2 text-xs font-medium outline-none">
                            {[15, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{m} min</option>)}
                          </select>
                          <select data-testid="admin-schedule-mode" value={schedForm.mode} onChange={(e) => setSchedForm({ ...schedForm, mode: e.target.value })} className="bg-[#F4F4F0] rounded-xl border-2 border-slate-900/10 px-3 py-2 text-xs font-medium outline-none">
                            <option value="video">Video call</option>
                            <option value="phone">Phone call</option>
                            <option value="onsite">On-site</option>
                          </select>
                          <input data-testid="admin-schedule-note" value={schedForm.note} onChange={(e) => setSchedForm({ ...schedForm, note: e.target.value })} placeholder="Note for the candidate (optional)" className="flex-1 min-w-[180px] bg-[#F4F4F0] rounded-xl border-2 border-slate-900/10 px-3 py-2 text-xs font-medium outline-none focus:border-[#2563EB]" />
                          <button
                            data-testid="admin-schedule-submit"
                            onClick={() => scheduleInterview(a.id)}
                            disabled={!schedForm.proposed_at || schedBusy}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2563EB] text-white text-xs font-bold hover:bg-[#0F172A] transition-colors disabled:opacity-50"
                          >
                            {schedBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />}
                            Send invite
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {schedMsg && (
                    <div data-testid="admin-schedule-message" className={`text-xs font-bold ${schedMsg.ok ? "text-[#10B981]" : "text-red-600"}`}>{schedMsg.text}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
